import React, { useEffect, useState } from "react";
import { Row, Col, Card, Statistic, Typography, List, Avatar, Badge, Spin } from "antd";
import { CalendarOutlined, TeamOutlined, ClockCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useAuth } from "../Context";
import { getEventosPorUsuario } from "../../servicios/agenda/servicios";
import dayjs from "dayjs";
import 'dayjs/locale/es';

const { Title, Text } = Typography;

// Componente para mostrar en la bienvenida
const ProximosEventos = () => {
  const { currentUser, sucursal, token } = useAuth();
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cargarEventos = async () => {
      if (!currentUser?.uid || !sucursal || !token) {
        setLoading(false);
        return;
      }

      try {
        // Obtenemos los periodos relevantes (mes actual y el siguiente)
        const hoy = dayjs();
        const periodoActual = `${hoy.year()}-${(hoy.month() + 1) < 10 ? '0' + (hoy.month() + 1) : hoy.month() + 1}`;
        
        const mesSiguiente = hoy.add(1, 'month');
        const periodoSiguiente = `${mesSiguiente.year()}-${(mesSiguiente.month() + 1) < 10 ? '0' + (mesSiguiente.month() + 1) : mesSiguiente.month() + 1}`;
        
        // Obtener eventos del usuario actual para ambos periodos
        const [eventosActual, eventosSiguiente] = await Promise.all([
          getEventosPorUsuario(currentUser.uid, sucursal, token, periodoActual),
          getEventosPorUsuario(currentUser.uid, sucursal, token, periodoSiguiente)
        ]);
        
        // Combinamos todos los eventos
        const todosEventos = [...eventosActual, ...eventosSiguiente];
        
        // Filtrar solo los próximos eventos (de hoy en adelante) y ordenar por fecha
        const fechaHoy = dayjs().format('YYYY-MM-DD');
        const proximosEventos = todosEventos
          .filter(evento => evento.fecha >= fechaHoy)
          .sort((a, b) => {
            // Ordenar primero por fecha
            const fechaComparacion = a.fecha.localeCompare(b.fecha);
            if (fechaComparacion !== 0) return fechaComparacion;
            
            // Si tienen la misma fecha, ordenar por hora de inicio
            if (a.horaInicio && b.horaInicio) {
              return a.horaInicio.localeCompare(b.horaInicio);
            } else if (a.horaInicio) {
              return -1;
            } else if (b.horaInicio) {
              return 1;
            }
            return 0;
          })
          .slice(0, 5); // Mostrar solo los 5 próximos eventos
          
        setEventos(proximosEventos);
      } catch (error) {
        console.error("Error al cargar eventos en la bienvenida:", error);
      } finally {
        setLoading(false);
      }
    };

    cargarEventos();
  }, [currentUser, sucursal, token]);

  const formatoFecha = (fecha) => {
    return dayjs(fecha).locale('es').format('DD [de] MMMM [de] YYYY');
  };

  return (
    <Card 
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CalendarOutlined style={{ marginRight: 8, color: '#1890ff' }} /> 
          <span>Próximos Eventos</span>
        </div>
      } 
      style={{ height: '100%' }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : eventos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Text type="secondary">No tienes eventos próximos en la agenda</Text>
        </div>
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={eventos}
          renderItem={(evento) => (
            <List.Item>
              <List.Item.Meta
                avatar={
                  <Avatar 
                    style={{ backgroundColor: evento.color || '#1890ff' }} 
                    icon={<CalendarOutlined />} 
                  />
                }
                title={
                  <Row justify="space-between">
                    <Col>
                      <Badge color={evento.color || '#1890ff'} text={evento.titulo} />
                    </Col>
                    <Col>
                      <Text type="secondary" style={{ fontSize: '0.9em' }}>
                        {formatoFecha(evento.fecha)}
                      </Text>
                    </Col>
                  </Row>
                }
                description={
                  <>
                    {evento.horaInicio && (
                      <div>
                        <ClockCircleOutlined style={{ marginRight: 5 }} /> 
                        {evento.horaInicio.substring(0, 5)} 
                        {evento.horaFin && ` - ${evento.horaFin.substring(0, 5)}`}
                      </div>
                    )}
                    {evento.ubicacion && (
                      <div>
                        <EnvironmentOutlined style={{ marginRight: 5 }} /> 
                        {evento.ubicacion}
                      </div>
                    )}
                    {evento.participantes && evento.participantes.length > 0 && (
                      <div>
                        <TeamOutlined style={{ marginRight: 5 }} /> 
                        {evento.participantes.length} participante{evento.participantes.length !== 1 ? 's' : ''}
                      </div>
                    )}
                    {evento.descripcion && (
                      <div style={{ marginTop: 5 }}>
                        {evento.descripcion.length > 120 
                          ? `${evento.descripcion.substring(0, 120)}...` 
                          : evento.descripcion
                        }
                      </div>
                    )}
                  </>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};

export default ProximosEventos;
